/**
 * FOI-538 — Product quiz scoring engine (model v2.2). Pure functions, no DOM,
 * no fetch.
 *
 * computeRecommendation(answers, rules) reproduces the "Foil Drive Quiz Logic
 * v2.2" sheet calculation — proven against the sheet's own Test & Preview
 * output by tests/quiz-engine.test.js.
 *
 * score(product, component) =
 *   Σ over active questions ( importance / Σ importances × weight )
 * Discipline multi-select (≤3): the selected disciplines' weight rows are
 * averaged. Battery/motor incompatible with the winning system are forced to 0
 * BEFORE ranking.
 *
 * v2.2 structural rules:
 * - Q7 mast is SCORED (importance 3.0 on motor and cable), not a switch. The
 *   integrated motor wins the integrated path through its mast weight row —
 *   there is no hardcoded product swap here any more.
 * - Build type is read back off the winning motor:
 *   flags.integratedMast = (winning motor === rules.integratedMotorKey).
 *   This is what makes the "Not sure" mast answer resolve sensibly.
 * - `motorHeight` is ALWAYS set (the cable-ranking winner). It says where the
 *   motor sits on the mast. A cable to BUY is a separate thing: Retro-Fit path
 *   only, and flagged `included` when it is the 20cm that ships in the kit.
 * - Accessories: 2 slots (prop upgrade, guard), max 2 recommended, at or above
 *   rules.accessoryThreshold. No blade hubs, no guard compatibility rule.
 *
 * Interpretation note (dual cable): the sheet says disciplines spanning the
 * short and long groups get BOTH cables, without spelling out how the two
 * sizes are picked. Implemented as: best-scoring short size (12–30) +
 * best-scoring long size (60/70). CONFIRMED 2026-09-07 on QC bug #188 — the
 * sheet's own "Cable plan" row names 15cm + 70cm for a rider spanning both
 * groups, which is what this produces.
 *
 * Input `answers`:
 *   { ability, assistance, weight, priority, budget, mast: '<option key>',
 *     discipline: ['<option key>', … up to 3] }
 * Answers are UI state; product data and store config are NOT inputs —
 * variant resolution happens in pd-quiz.js against pd-quiz-config data.
 */
(function () {
  'use strict';

  // Every score is a sum of floating-point products, so two values the sheet
  // says are equal can come back differing in the last bit
  // (0.712 vs 0.7120000000000001). Anything closer together than this counts
  // as a tie, and a tie is decided by the rules-file product order — never by
  // which sum happened to round up. The same epsilon guards the accessory
  // threshold, where a hand-computed 0.4 arrives as 0.39999999999999997.
  var TIE_EPSILON = 1e-9;

  // Sheet tab 10: at most two accessories are ever recommended. There are two
  // slots today so the cap is never reached; it stays as the explicit promise
  // the sweep test asserts, so adding a third slot cannot quietly show three.
  var MAX_ACCESSORIES = 2;

  // The five components the rules file must describe. Anything missing here is
  // a broken rules object, not a recoverable input.
  var SCORED_COMPONENTS = ['system', 'battery', 'motor', 'cable', 'accessory'];

  var hasOwn = Object.prototype.hasOwnProperty;

  function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  // A short, readable name for a value in an error message. Errors here are
  // read by whoever wired the quiz up, so "an array" beats "[object Object]".
  function describe(value) {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (Array.isArray(value)) return 'an array';
    if (typeof value === 'string') return 'the string "' + value + '"';
    if (typeof value === 'object') return 'an object';
    return typeof value + ' ' + String(value);
  }

  // The rules object is the whole model. A half-built one used to escape as a
  // raw TypeError three or four lines later ("Cannot read properties of
  // undefined (reading 'system')"), which says nothing about what is wrong.
  function validateRules(rules) {
    var bad = function (detail) {
      return new Error(
        'computeRecommendation(answers, rules): rules must be the pd-quiz-rules object — ' + detail
      );
    };
    if (!isPlainObject(rules)) throw bad('got ' + describe(rules));
    if (!Array.isArray(rules.questions) || rules.questions.length === 0) {
      throw bad('rules.questions must be a non-empty array of questions');
    }
    if (!isPlainObject(rules.components)) throw bad('rules.components is missing');
    SCORED_COMPONENTS.forEach(function (name) {
      var component = rules.components[name];
      if (
        !isPlainObject(component) ||
        !Array.isArray(component.products) ||
        !isPlainObject(component.importance) ||
        !isPlainObject(component.weights)
      ) {
        throw bad('rules.components.' + name + ' must have products, importance and weights');
      }
      // An EMPTY products array used to validate fine and then return a build
      // with `system: undefined` in it, the rest of the kit still filled in.
      // A wrong answer, not an error (FOI-538 QC round 3, F7).
      if (component.products.length === 0) {
        throw bad('rules.components.' + name + '.products is empty — there is nothing to score');
      }
    });
    if (
      !isPlainObject(rules.compatibility) ||
      !isPlainObject(rules.compatibility.battery) ||
      !isPlainObject(rules.compatibility.motor)
    ) {
      throw bad('rules.compatibility.battery and rules.compatibility.motor are required');
    }

    // The three keys below are read AFTER scoring, so a missing one never threw
    // — it produced a quietly wrong build instead. All three are diagnostics:
    // only a broken rules file can reach them, and tests/quiz-rules.test.js
    // pins the real one. A silent wrong answer is still worse than a crash
    // (FOI-538 QC round 3, F7).

    // Missing, every accessory score was compared against `undefined`. That
    // comparison is always false, so NO shopper was ever offered an accessory.
    if (typeof rules.accessoryThreshold !== 'number' || !isFinite(rules.accessoryThreshold)) {
      throw bad(
        'rules.accessoryThreshold must be a finite number, got ' + describe(rules.accessoryThreshold)
      );
    }
    // Missing, `flags.integratedMast` was false for every shopper, so an
    // integrated build was shown a cable it does not need and cannot use.
    if (
      typeof rules.integratedMotorKey !== 'string' ||
      rules.components.motor.products.indexOf(rules.integratedMotorKey) === -1
    ) {
      throw bad(
        'rules.integratedMotorKey must be one of rules.components.motor.products, got ' +
        describe(rules.integratedMotorKey)
      );
    }
    // Missing a SLOT, that slot's accessory silently dropped out of every
    // recommendation. Not on QC's round-3 list, but the same failure mode as
    // the two above and in the same read, so it is closed here too.
    if (!isPlainObject(rules.accessorySlots)) {
      throw bad('rules.accessorySlots must be an object of slot name → product keys');
    }
    Object.keys(rules.accessorySlots).forEach(function (slot) {
      if (!Array.isArray(rules.accessorySlots[slot]) || rules.accessorySlots[slot].length === 0) {
        throw bad('rules.accessorySlots.' + slot + ' must be a non-empty array of accessory product keys');
      }
    });
  }

  function validateAnswers(answers, rules) {
    if (!answers || typeof answers !== 'object' || Array.isArray(answers)) {
      throw new Error('computeRecommendation(answers, rules): answers must be an object of question key → answer');
    }
    rules.questions.forEach(function (question) {
      var value = answers[question.key];
      if (question.type === 'multi') {
        if (!Array.isArray(value) || value.length === 0) {
          throw new Error('Missing answer for "' + question.key + '" (expected a non-empty array)');
        }
        if (value.length > question.max) {
          throw new Error('"' + question.key + '" allows max ' + question.max + ' selections, got ' + value.length);
        }
        var seen = {};
        // Read by index, not with forEach. forEach SKIPS an array hole, so a
        // sparse array such as `Object.assign([], {1:'surf_foil', length:2})`
        // used to validate as one clean answer while weightFor still divided
        // the total by length 2 — halving every discipline weight in silence.
        for (var i = 0; i < value.length; i++) {
          var picked = value[i];
          if (typeof picked !== 'string' || picked.length === 0) {
            throw new Error(
              '"' + question.key + '" has an empty slot at position ' + (i + 1) +
              ' — every selection must be an option key'
            );
          }
          // The UI cannot produce a repeat, but the engine is the contract: a
          // repeat would quietly double that row's weight in the average.
          if (hasOwn.call(seen, picked)) {
            throw new Error('Duplicate answer "' + picked + '" for question "' + question.key + '"');
          }
          seen[picked] = true;
        }
      } else {
        if (typeof value !== 'string' || value.length === 0) {
          throw new Error('Missing answer for "' + question.key + '"');
        }
        value = [value];
      }
      var optionKeys = question.options.map(function (o) { return o.key; });
      value.forEach(function (key) {
        if (optionKeys.indexOf(key) === -1) {
          throw new Error('Unknown answer "' + key + '" for question "' + question.key + '"');
        }
      });
    });
  }

  // Weight of one answer (or the average of multi-select answers) for one
  // product. A blank sheet cell is an absent key here → 0.
  function weightFor(byAnswer, answerKeys, productKey) {
    var total = 0;
    answerKeys.forEach(function (answerKey) {
      var row = byAnswer[answerKey];
      var value = row && row[productKey] !== undefined ? row[productKey] : 0;
      total += value;
    });
    return total / answerKeys.length;
  }

  // Raw importance-share score map for one component: { productKey: 0..1 }.
  function scoreComponent(component, answers) {
    var totalImportance = 0;
    Object.keys(component.importance).forEach(function (qKey) {
      totalImportance += component.importance[qKey];
    });

    var scores = {};
    component.products.forEach(function (productKey) {
      var score = 0;
      Object.keys(component.importance).forEach(function (qKey) {
        var share = component.importance[qKey] / totalImportance;
        var byAnswer = component.weights[qKey];
        if (!byAnswer) return; // scored block with no weight rows at all
        var answerKeys = Array.isArray(answers[qKey]) ? answers[qKey] : [answers[qKey]];
        score += share * weightFor(byAnswer, answerKeys, productKey);
      });
      scores[productKey] = score;
    });
    return scores;
  }

  // Product keys sorted by score (highest first). Ties keep rules-file order:
  // Array#sort is stable, so returning 0 leaves the two keys where they were.
  // The epsilon is what makes that rule real — without it, 1.1e-16 of float
  // noise decides the winner instead of the documented order.
  function rank(scores, productOrder) {
    return productOrder.slice().sort(function (a, b) {
      var gap = scores[b] - scores[a];
      return Math.abs(gap) < TIE_EPSILON ? 0 : gap;
    });
  }

  function applyCompatibility(scores, compatibilityRows, winningSystem) {
    var result = {};
    Object.keys(scores).forEach(function (productKey) {
      var row = compatibilityRows[productKey];
      var compatible = row && row[winningSystem] === 1;
      result[productKey] = compatible ? scores[productKey] : 0;
    });
    return result;
  }

  // The cable size a rider should ORDER to land the recommended motor height.
  // Fusion's Gen2.5 plug sits ~5cm further from the mast, so Fusion orders one
  // step longer (fixed sheet lookup, not a blind +5).
  function resolveCableOrder(effectiveSize, systemKey, rules) {
    if (systemKey === 'Fusion') {
      return rules.fusionCableLookup[effectiveSize];
    }
    return effectiveSize;
  }

  // Highest-scoring key out of a slot's candidate list. A candidate with no
  // real score is skipped, not accepted: the first candidate used to win by
  // default, which returned a key the component never scored. Ties keep the
  // candidate-list order, which the rules tests pin to `products` order so
  // this and rank() cannot disagree.
  function bestOf(scores, candidateKeys) {
    var best = null;
    (candidateKeys || []).forEach(function (key) {
      var score = scores[key];
      if (typeof score !== 'number' || !isFinite(score)) return;
      if (best === null || score > scores[best] + TIE_EPSILON) best = key;
    });
    return best;
  }

  // The cable list for one build: none on the integrated path, two when the
  // disciplines span both size groups, else the motor height.
  function buildCables(cableScores, motorHeight, integratedMast, dualCable, systemKey, rules) {
    if (integratedMast) return [];
    var effectiveSizes = dualCable
      ? [bestOf(cableScores, rules.cableSizeGroups.short), bestOf(cableScores, rules.cableSizeGroups.long)]
      : [motorHeight];
    return effectiveSizes.map(function (effective) {
      var order = resolveCableOrder(effective, systemKey, rules);
      // "Included" is about the size the rider would ORDER, not the
      // effective height: a Fusion rider at 20cm orders 25cm and must buy it.
      return { effective: effective, order: order, included: order === rules.includedInKit.cable };
    });
  }

  function computeRecommendation(answers, rules) {
    validateRules(rules);
    validateAnswers(answers, rules);

    var components = rules.components;

    // 1. System — the winner drives every compatibility rule after it.
    var systemScores = scoreComponent(components.system, answers);
    var systemRanking = rank(systemScores, components.system.products);
    var winningSystem = systemRanking[0];

    // 2. Battery + motor — compatibility-zeroed against the winning system.
    var baseBatteryScores = scoreComponent(components.battery, answers);
    var batteryScores = applyCompatibility(
      baseBatteryScores,
      rules.compatibility.battery,
      winningSystem
    );
    var batteryRanking = rank(batteryScores, components.battery.products);

    var baseMotorScores = scoreComponent(components.motor, answers);
    var motorScores = applyCompatibility(
      baseMotorScores,
      rules.compatibility.motor,
      winningSystem
    );
    var motorRanking = rank(motorScores, components.motor.products);
    var winningMotor = motorRanking[0];

    // The build type falls out of the motor that won — no special case for the
    // "Not sure" mast answer.
    var integratedMast = winningMotor === rules.integratedMotorKey;

    // 3. Motor height — always reported, both build types. On the Retro-Fit
    //    path the same ranking also decides which cable to buy.
    var cableScores = scoreComponent(components.cable, answers);
    var cableRanking = rank(cableScores, components.cable.products);
    var motorHeight = cableRanking[0];

    var disciplines = answers.discipline;
    var spansShort = disciplines.some(function (d) { return rules.cableGroups.short.indexOf(d) !== -1; });
    var spansLong = disciplines.some(function (d) { return rules.cableGroups.long.indexOf(d) !== -1; });
    var dualCable = !integratedMast && spansShort && spansLong;

    // The Integrated Mast build has no cable at all — the motor height is the
    // whole answer there.
    var cables = buildCables(cableScores, motorHeight, integratedMast, dualCable, winningSystem, rules);

    // 4. Accessories — 2 mutually-exclusive slots, max 2 recommended, only at
    //    or above the threshold. 0.4 exactly passes.
    var accessoryScores = scoreComponent(components.accessory, answers);
    var accessoryRanking = rank(accessoryScores, components.accessory.products);
    var slotWinners = [
      bestOf(accessoryScores, rules.accessorySlots.propUpgrade),
      bestOf(accessoryScores, rules.accessorySlots.guard)
    ];
    var accessories = slotWinners
      .filter(function (key) {
        // "At or above" (sheet tab 10). A score the sheet says IS 0.4 can
        // arrive as 0.39999999999999997, so the comparison has to allow the
        // same tie epsilon the ranking uses.
        return key !== null && accessoryScores[key] >= rules.accessoryThreshold - TIE_EPSILON;
      })
      .sort(function (a, b) {
        var gap = accessoryScores[b] - accessoryScores[a];
        return Math.abs(gap) < TIE_EPSILON ? 0 : gap;
      })
      .slice(0, MAX_ACCESSORIES);

    return {
      scores: {
        system: systemScores,
        battery: batteryScores,
        motor: motorScores,
        cable: cableScores,
        accessory: accessoryScores
      },
      rankings: {
        system: systemRanking,
        battery: batteryRanking,
        motor: motorRanking,
        cable: cableRanking,
        accessory: accessoryRanking
      },
      system: winningSystem,
      battery: batteryRanking[0],
      motor: winningMotor,
      motorHeight: motorHeight,
      cables: cables,
      accessories: accessories,
      flags: {
        integratedMast: integratedMast,
        dualCable: dualCable,
        fusionCableBump: cables.some(function (c) { return c.order !== c.effective; })
      }
    };
  }

  var PdQuizEngine = {
    computeRecommendation: computeRecommendation,
    resolveCableOrder: resolveCableOrder,
    // Exported so the tests compare against the same tie epsilon the engine
    // uses, instead of keeping their own copy of the number.
    TIE_EPSILON: TIE_EPSILON
  };

  if (typeof window !== 'undefined') {
    window.PdQuizEngine = PdQuizEngine;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = PdQuizEngine;
  }
})();
